import { Fragment as _Fragment, jsx as _jsx } from "react/jsx-runtime";
/**
 * Recursive walker that turns a parsed scene into a React tree, one registered
 * component per node, loading instanced sub-scenes as it goes. It mounts no
 * `<Canvas>` and no context provider: `<TscnCanvas>` and `<TscnPreviewShell>` own those.
 */
import { Fragment, useCallback, useEffect, useMemo } from 'react';
import { belowRoot, joinPath } from '../utils/nodePath.js';
import { allocateNodePaintRange, allocatePaintRange, canvasRootRanges, isCanvasLayerType, canvasLayerRanks, hostsOwnCanvas, WHOLE_CANVAS_RANGE, } from './canvasPaintOrder.js';
import { isViewportBoundary } from '../nodes/viewport/subviewport/viewportBoundary.js';
import { CanvasRootRangesProvider, LayerRanksProvider, PaintRangeProvider, useCanvasRootRanges, usePaintRange, } from './contexts/PaintOrderContext.js';
import { nodeComponentRegistry } from './NodeComponentRegistry.js';
import { useViewportSelection } from './hooks/useViewportSelection.js';
import { useCanvasWorkspace } from './contexts/CanvasWorkspaceContext.js';
import { drawsInWorkspace } from './nodeWorkspaceVisibility.js';
import { useAnimatedValueAt } from './contexts/AnimatedValueContext.js';
import { NodePathProvider } from './contexts/NodePathContext.js';
import { childOrders, TreeOrderProvider, useTreeOrder } from './contexts/TreeOrderContext.js';
import { VisibilityParentScope } from './visibilityRange/VisibilityParentContext.js';
import { InstancedScenePathsProvider, useInstancedScenePaths, } from './contexts/InstancedScenePathsContext.js';
import { InstanceOwnerProvider } from './contexts/InstanceOwnersContext.js';
import { useLiveStepOf } from './useLiveSceneTree.js';
import { useLiveDrawnNode } from './liveNodePatches.js';
import { useResourceLoader } from '../resources/useResource.js';
import { resolveInstancePath } from '../resources/SubResourceResolver.js';
import { useInstancedScene } from './hooks/useInstancedScene.js';
import { SceneResourcesProvider, useSceneResources } from './SceneResourcesContext.js';
import { useSelection } from './contexts/SelectionContext.js';
import { MissingResourcePlaceholder } from './components/MissingResourcePlaceholder.js';
import { GuardedNodeComponent } from './components/GuardedNodeComponent.js';
import { NodeEditorIcon } from './nodeIcon/NodeEditorIcon.js';
import { useCanvasModulateColor, CanvasModulateContext } from './canvasModulate.js';
import { SpriteBase3DChildAccum } from './spriteBase3DColorAccum.js';
import { CanvasLayerScope } from './canvasLayerScope.js';
import { CanvasRootScope } from './canvasRootScope.js';
import { ParentTypeProvider, ParentSpaceScope, TopLevelScope, useParentSpaceFamily, WorldRoot, } from './parentSpaceScope.js';
import { GlbInstanceProvider } from './internal/glb-scene-root/GlbInstanceContext.js';
import { liftedWithoutOverrides } from './internal/glb-scene-root/glbNodeOverrides.js';
import { glbHostChildrenInOrder } from './internal/glb-scene-root/glbHierarchy.js';
import { useGlb } from '../resources/useGlb.js';
import { GlbRootNode } from './internal/glb-scene-root/GlbRootNode.js';
import { loneGlbRootPath } from '../resources/glbSceneRoot.js';
import { prefetchCsgModule } from './csg/csgModule.js';
import { warn } from '../logger.js';
import { spaceFamilyOf } from '../godot/scene/parentSpace.js';
/** Does this subtree contain anything that needs boolean evaluation? */
function containsCsgShape(node) {
    if (nodeComponentRegistry.isCsgShape(node.type))
        return true;
    return node.children.some(containsCsgShape);
}
/**
 * Whether Godot hides `node` itself: a Node3D whose `visible` is false hides its Node3D descendants
 * too (`node_3d.cpp:1132-1143`). A descendant of another family escapes to the world root first
 * (`parentSpaceScope.tsx`), so the wrapper hides only what Godot hides. `animatedVisible` is the
 * value an AnimationPlayer track pushes, null when none does.
 */
function isHiddenNode3D(node, animatedVisible) {
    if (spaceFamilyOf(node.type) !== 'Node3D')
        return false;
    return !(animatedVisible ?? node.properties.visible !== false);
}
export function NodeDispatcher({ nodes }) {
    const { handlers } = useViewportSelection();
    // Fetch the CSG library early, so a boolean result reaches the opening frame
    // sooner: CameraFit re-frames it only once the loader has settled.
    // Evaluation lives in CsgPrimitive: skipping CSG children here strips their selection.
    useEffect(() => {
        if (nodes.some(containsCsgShape))
            prefetchCsgModule();
    }, [nodes]);
    const canvasModulate = useCanvasModulateColor(nodes);
    // Pure functions of the tree, so the Control walk derives the same numbers on its own.
    const ranks = useMemo(() => canvasLayerRanks(nodes), [nodes]);
    const rootRanges = useMemo(() => allocatePaintRange(WHOLE_CANVAS_RANGE, nodes).children, [nodes]);
    // Where each canvas root of the viewport's own canvas draws: an item whose
    // parent is not a CanvasItem is drawn in its pre-order rank among the
    // canvas's roots, not at the slot its nesting gives it (`canvasRootRanges`).
    const canvasRoots = useMemo(() => canvasRootRanges(nodes, rootRanges), [nodes, rootRanges]);
    const rootOrders = useMemo(() => childOrders([], nodes.length), [nodes.length]);
    return (_jsx("group", { onPointerDown: handlers.onPointerDown, onPointerUp: handlers.onPointerUp, onPointerMove: handlers.onPointerMove, onPointerOut: handlers.onPointerOut, children: _jsx(CanvasModulateContext.Provider, { value: canvasModulate, children: _jsx(LayerRanksProvider, { value: ranks, children: _jsx(CanvasRootRangesProvider, { value: canvasRoots, children: _jsx(WorldRoot, { children: nodes.map((node, i) => (_jsx(PaintRangeProvider, { value: canvasRoots.get(node) ?? rootRanges[i], children: _jsx(TreeOrderProvider, { order: rootOrders[i], children: _jsx(DispatchedNode, { node: node, path: node.name }) }) }, node.name))) }) }) }) }) }));
}
/**
 * Sends a node with an `instance` ref to `InstancedNode` and every other node to
 * `PlainNode`. It holds no hooks, so the branch is free of rules-of-hooks concerns.
 */
export function DispatchedNode({ node, path }) {
    const dispatched = node.instance ? (_jsx(InstancedNode, { node: node, path: path })) : (_jsx(PlainNode, { node: node, path: path }));
    return node.scope ? _jsx(OwnResourceScope, { scope: node.scope, children: dispatched }) : dispatched;
}
/**
 * Provides the scope a node carries (`LiveNode.scope`). Under another scene's provider a
 * grafted node's `ExtResource("3")` and `SubResource("1")` name a different resource or
 * none. The provider prepends onto the ambient pool, so the node's own scope wins an id
 * both hold. Its enclosing scenes come too, so the node may instance the scene it sits in.
 */
function OwnResourceScope({ scope, children }) {
    const resources = (_jsx(SceneResourcesProvider, { externalResources: scope.externalResources, internalResources: scope.internalResources, children: children }));
    return scope.instancedScenePaths ? (_jsx(InstancedScenePathsProvider, { paths: scope.instancedScenePaths, children: resources })) : (resources);
}
/**
 * The node without the children `isDropped` names, and the same reference when it names none, so
 * the `useMemo([node])`s in `PlainNode` stay valid. `some` comes before `filter` since most nodes
 * drop nothing.
 */
function withoutChildrenWhere(node, isDropped) {
    if (!node.children.some(isDropped))
        return node;
    return { ...node, children: node.children.filter((child) => !isDropped(child)) };
}
/**
 * The node without its deep children, whose parent path descends into this
 * instance's content. Here they would take the instance's transform, so they render
 * nowhere. `GlbInstanceProvider` still gets them all, and `GlbAttachedChildren` mounts each
 * typed one under the GLB object its path names.
 */
function withoutDeepChildren(node) {
    return withoutChildrenWhere(node, (child) => !!child.instanceSubPath);
}
/**
 * The node without its property overrides and what is written below them. Where the instanced
 * scene is unknown or missing, nothing takes an override, so it draws nothing: Godot skips a node
 * it cannot find (`packed_scene.cpp:310`).
 */
function withoutOverrideSubtrees(node) {
    return withoutChildrenWhere(node, (child) => child.overridesExistingNode === true);
}
/**
 * The node without its property overrides, `[node name="Torso" parent="."]`, and with the nodes
 * written below them lifted to the instance. Godot takes the existing node by name and adds none
 * (`packed_scene.cpp:280-283`), so an override of an instanced GLB's object draws no node:
 * `GlbInstanceProvider` hands it to the GLB, which applies it.
 */
function withoutPropertyOverrides(node) {
    if (!node.children.some((child) => child.overridesExistingNode))
        return node;
    return { ...node, children: liftedWithoutOverrides(node.children) };
}
/**
 * Renders one non-instance node into its wrapper `<group>` and registered
 * component, with its inline children and the `extraChildren` of the instance
 * fallback. A merged instance root renders here like an authored node.
 */
function PlainNode({ node: authored, path, children: extraChildren, inlineChildrenOrder, }) {
    // A RemoteTransform target and a path-mode CSGPolygon3D read what the live scene passes bind
    // at their path, since a sub-scene's node is shared by every instance of it (ADR-0060).
    const node = useLiveDrawnNode(authored, path);
    const ownOrder = useTreeOrder();
    const inlineBase = inlineChildrenOrder ?? ownOrder;
    const inlineOrders = useMemo(() => childOrders(inlineBase, node.children.length), [inlineBase, node.children.length]);
    const { hiddenNodePaths, registerNodeObject, unregisterNodeObject } = useSelection();
    const workspace = useCanvasWorkspace();
    const paintRange = usePaintRange();
    // Before the early returns, for rules-of-hooks. Memoized: `allocateNodePaintRange`
    // walks each child's whole subtree, which on every render is quadratic in depth.
    // A sub-viewport is a canvas of its own, whose runs start at the beginning, as its Control walk's do.
    const allocated = useMemo(() => allocateNodePaintRange(isViewportBoundary(node.type) ? WHOLE_CANVAS_RANGE : paintRange, node), [paintRange, node]);
    // A pushed `visible` takes the place of the authored one, as a GLB node's does.
    const animatedVisible = useAnimatedValueAt(path, 'visible', (value) => value[0] !== 0);
    const isHidden = hiddenNodePaths.has(path) || isHiddenNode3D(node, animatedVisible);
    const inheritedCanvasRoots = useCanvasRootRanges();
    // A CanvasLayer is a canvas of its own, so the roots below it are indexed by
    // its counter over its children (`canvas_layer.cpp:261-267`). So is a sub-viewport.
    const startsCanvas = isCanvasLayerType(node.type);
    const hostsCanvas = hostsOwnCanvas(node);
    const canvasRoots = useMemo(() => (hostsCanvas ? canvasRootRanges(node.children, allocated.children) : inheritedCanvasRoots), [hostsCanvas, node.children, allocated.children, inheritedCanvasRoots]);
    // Whether `Object::cast_to<CanvasItem>(get_parent())` succeeds for this node.
    const parentIsCanvasItem = useParentSpaceFamily() === 'CanvasItem';
    const wrapperRef = useCallback((object) => {
        if (object) {
            registerNodeObject(path, object);
        }
        else {
            unregisterNodeObject(path);
        }
    }, [path, registerNodeObject, unregisterNodeObject]);
    if (!drawsInWorkspace(node.type, workspace))
        return null;
    // Each child gets its own run of draw-sequence values in Godot's walk order:
    // `show_behind_parent` children before this node, the rest after it. A run
    // covers the child's whole subtree, so nothing reaches a sibling's sequence.
    const inlineChildren = node.children.map((child, i) => (_jsx(PaintRangeProvider, { value: canvasRoots.get(child) ?? allocated.children[i], children: _jsx(TreeOrderProvider, { order: inlineOrders[i], children: _jsx(DispatchedNode, { node: child, path: joinPath(path, child.name) }) }) }, child.name)));
    const children = [];
    // Inside the node's own group, as a child, so the icon sits at its origin whatever the component draws.
    const editorIcon = nodeComponentRegistry.editorIconOf(node.type);
    if (editorIcon) {
        children.push(_jsx(NodeEditorIcon, { node: node, registration: editorIcon }, "__icon"));
    }
    if (inlineChildren.length > 0) {
        children.push(_jsx(Fragment, { children: inlineChildren }, "__inline"));
    }
    if (extraChildren) {
        // Injected sub-scene roots draw after the authored children, in the room
        // `reservesRoom` holds back for them.
        children.push(_jsx(PaintRangeProvider, { value: allocated.tail, children: extraChildren }, "__extra"));
    }
    // The wrapper is registered both ways: the pointer root resolves a hit mesh to
    // its nearest registered ancestor, and the highlights find a path's Object3D.
    return (_jsx(ParentSpaceScope, { node: node, children: _jsx(NodePathProvider, { path: path, children: _jsx(VisibilityParentScope, { node: node, path: path, children: _jsx("group", { ref: wrapperRef, visible: !isHidden, children: _jsx(TopLevelScope, { node: node, children: _jsx(CanvasRootScope, { node: node, parentIsCanvasItem: parentIsCanvasItem, children: _jsx(ParentTypeProvider, { value: node.type, children: _jsx(GuardedNodeComponent, { node: node, children: children.length > 0 ? (_jsx(SpriteBase3DChildAccum, { node: node, children: startsCanvas ? (_jsx(CanvasLayerScope, { node: node, children: _jsx(WorldRoot, { children: _jsx(CanvasRootRangesProvider, { value: canvasRoots, children: children }) }) })) : hostsCanvas ? (_jsx(CanvasRootRangesProvider, { value: canvasRoots, children: children })) : (_jsx(_Fragment, { children: children })) })) : null }) }) }) }) }) }) }) }));
}
/**
 * The two groups of a GLB instance's children in tree order. Godot adds the sub-scene's own
 * nodes when it instantiates the sub-scene, before the instancing scene adds its children
 * (`packed_scene.cpp:231-264`, `:535-541`).
 */
const GLB_OBJECTS = 0;
const INSTANCING_SCENE_NODES = 1;
/**
 * Loads the PackedScene a node's `instance` ref names and composes it into the
 * tree. A single-root `.tscn` merges into the instance node at the same path
 * (ADR-0013). A `.glb` is the instance node's own content, mounted at its path. Either way the
 * loaded scene's resources are scoped to the subtree. An instance of a scene that already encloses
 * it (cyclic instancing), or of one Godot refuses to instantiate, renders as a failed load.
 */
function InstancedNode({ node, path }) {
    const ambientScope = useSceneResources();
    const { externalResources } = ambientScope;
    const enclosingScenePaths = useInstancedScenePaths();
    const paintRange = usePaintRange();
    const instanceRef = node.instance ?? '';
    const scenePath = resolveInstancePath(instanceRef, externalResources);
    const isCyclic = scenePath !== null && enclosingScenePaths.includes(scenePath);
    // Null for a scene this node never loads: an unresolvable ref or a cycle.
    const loadPath = isCyclic ? null : scenePath;
    // Registered here, not in a parent: React runs a parent's effect after this
    // component's load effect.
    const result = useInstancedScene(instanceRef, externalResources, loadPath);
    const loadedScene = result.status === 'loaded' ? (result.value ?? null) : null;
    // The scenes that enclose the content of a `.glb` instance. A stable identity,
    // since it keys the provider below.
    const contentScenePaths = useMemo(() => (loadPath ? [...enclosingScenePaths, loadPath] : enclosingScenePaths), [enclosingScenePaths, loadPath]);
    // The same merge the tree, inspector and panels make. `effective !== node`
    // means a single root `.tscn` merged in. A `.glb` or a refused scene returns `node`.
    // The live tree hands back the same step until a load changes it, so a hover elsewhere
    // does not re-merge this subtree.
    const scope = useMemo(() => ({ ...ambientScope, instancedScenePaths: enclosingScenePaths }), [ambientScope, enclosingScenePaths]);
    const step = useLiveStepOf(node, scope);
    const effective = step.node;
    const isRefused = isCyclic || step.isRefusedInstance;
    useEffect(() => {
        if (isRefused)
            warn(`[NodeDispatcher] "${path}" instances ${scenePath}, which Godot refuses`);
    }, [isRefused, path, scenePath]);
    const glbPath = loneGlbRootPath(loadedScene?.nodes ?? []);
    // Loaded here, not by the GLB root, for its objects: the host children move to their `index=`
    // among them (`packed_scene.cpp:541-544`). The root takes this load.
    const glb = useGlb(glbPath);
    const loader = useResourceLoader();
    // Memoized: `PlainNode` memoizes a subtree scan on node identity, and a stripped node is a new
    // object. The order is read only for a child that states an `index=`, from the loader's template,
    // whose children every consumer of the GLB shares.
    const shallow = useMemo(() => {
        const stripped = withoutDeepChildren(glbPath !== undefined ? withoutPropertyOverrides(node) : node);
        if (!glb.value || !stripped.children.some((child) => child.index !== undefined))
            return stripped;
        const objects = (glbPath && loader?.glbMeshes.getCached(glbPath)) || glb.value;
        return { ...stripped, children: glbHostChildrenInOrder(objects, stripped.children) };
    }, [node, glbPath, glb.value, loader]);
    // What the node draws while its instanced scene is unknown or missing. Memoized like `shallow`.
    const held = useMemo(() => withoutDeepChildren(withoutOverrideSubtrees(node)), [node]);
    const order = useTreeOrder();
    const groupOrders = useMemo(() => childOrders(order, 2), [order]);
    // The GLB root draws from the tail of `shallow`'s range, as an authored child gets a run of its own.
    const glbRange = useMemo(() => allocatePaintRange(allocateNodePaintRange(paintRange, shallow).tail, loadedScene?.nodes ?? [])
        .children[0], [paintRange, shallow, loadedScene?.nodes]);
    // Unresolvable ref, cycle, refused scene or failed load: keep the node visible with a magenta
    // placeholder child, matching the missing-texture UX. Godot's editor makes a `MissingNode` here
    // (`packed_scene.cpp:267-269`), which the host's own children hang below.
    if (!loadPath || result.status === 'unavailable' || step.isRefusedInstance) {
        return (_jsx(PlainNode, { node: held, path: path, children: _jsx(MissingResourcePlaceholder, { shape: "box" }) }));
    }
    // Still loading: render the instancing node's own subtree; the merged
    // result swaps in once the sub-scene arrives.
    if (result.status === 'pending' || !loadedScene) {
        return _jsx(PlainNode, { node: held, path: path });
    }
    // The merged node carries the sub-scene's scope, which `DispatchedNode` provides.
    if (effective !== node) {
        return (_jsx(InstanceOwnerProvider, { path: belowRoot(path), editablePaths: loadedScene.editableInstances, children: _jsx(DispatchedNode, { node: effective, path: path }) }));
    }
    // Not merged and not a GLB, so the live tree and the loader disagree on this scene: draw the
    // node as authored, not a non-GLB root through the GLB mount.
    if (glbPath === undefined)
        return _jsx(PlainNode, { node: held, path: path });
    // A `.glb` mounts at this node's path.
    return (_jsx(InstanceOwnerProvider, { path: belowRoot(path), editablePaths: loadedScene.editableInstances, children: _jsx(GlbInstanceProvider, { node: node, path: path, glb: glb, children: _jsx(PlainNode, { node: shallow, path: path, inlineChildrenOrder: groupOrders[INSTANCING_SCENE_NODES], children: _jsx(SceneResourcesProvider, { internalResources: loadedScene.internalResources, externalResources: loadedScene.externalResources, children: _jsx(InstancedScenePathsProvider, { paths: contentScenePaths, children: _jsx(PaintRangeProvider, { value: glbRange, children: _jsx(TreeOrderProvider, { order: [...groupOrders[GLB_OBJECTS], 0], children: _jsx(GlbRootNode, { node: loadedScene.nodes[0] }) }) }) }) }) }) }) }));
}
//# sourceMappingURL=NodeDispatcher.js.map